import { baseApi } from './base.api';
import { API_VERSION, PATH_ANALYTICS_API, PATH_PROJECTS_API } from '../constants/api-routes';
import { ProductKey } from 'src/constants/products';
import { getApiVersion } from 'src/utils/apiVersion';
import type { IntegrationSettings } from 'src/store/slices/integrationSlice';
import type { User } from 'src/services/usersService';

// Response shapes are only partially specified by the API; extra fields pass through untyped.
export interface ProjectTeamMember {
  id: number;
  user: Pick<User, 'id' | 'email' | 'first_name' | 'last_name' | 'role' | 'is_active'> & {
    // The user's helpdesk (CRM) account in each project, used for agent analytics.
    crm_data?: { project_id: number; crm_user_id?: number | string }[];
  };
  requests_left?: number;
  credit_count?: number;
}

export interface ProjectIntegration {
  id?: number;
  integration_id: string | number;
  integration_type?: string;
  integration_data?: IntegrationSettings;
  status?: string;
  [key: string]: unknown;
}

// Last month's summary for a project, merged into it on the home page when available.
export interface ProjectStatistics {
  accuracy?: number;
  accuracy_diff?: number;
  processed_tickets?: number;
  processed_tickets_diff?: number;
  resolution_rate?: number;
  resolution_rate_diff?: number;
  tickets_resolved_with_ai?: number;
  tickets_resolved_with_ai_diff?: number;
}

// The statistics summary: one entry per project, per product.
export interface ProjectsStatisticsSummary {
  cocustomer?: (ProjectStatistics & { project_id: number })[];
  coagent?: (ProjectStatistics & { project_id: number })[];
}

export interface ProjectFile {
  id: string | number;
  [key: string]: unknown;
}

export interface Project {
  id: number;
  name: string;
  team_members: ProjectTeamMember[];
  project_type: ProductKey;
  integrations: ProjectIntegration[];
  is_active: boolean;
  is_demo_mode?: boolean;
  cocbi_project_id: number;
  use_cocbi_model: boolean;
  ai_reply_generation: boolean;
  ai_text_lense: boolean;
  requests_left?: number;
  rate_limit_chat?: number;
  rate_limit_chat_per?: string;
  rate_limit_project?: number;
  rate_limit_project_per?: string;
  rate_limit_max_chats?: number;
  rate_limit_max_chats_per?: string;
  autoclose_time_window_minutes?: number | null;
  // Populated client-side by the files and chats endpoints' cache updates.
  files?: ProjectFile[];
  chats?: unknown;
}

export const projectsService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      getAllProjects: build.query<Project[], void>({
        query: () => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ALL}`,
          };
        },
        providesTags: ['Projects'],
        // v1 returns the list itself; v2 wraps it in a page.
        transformResponse(response: Project[] | { items: Project[] }) {
          return getApiVersion() === API_VERSION.V2
            ? (response as { items: Project[] }).items
            : (response as Project[]);
        },
      }),
      getProjectsStatisticsSummary: build.query<
        ProjectsStatisticsSummary,
        {
          start_date: string;
          end_date: string;
          // Query strings of repeated project id params, joined with '&'.
          cocustomer_project_ids: string;
          coagent_project_ids: string;
        }
      >({
        query: ({ start_date, end_date, cocustomer_project_ids, coagent_project_ids }) => ({
          url: `${API_VERSION.V1}/${PATH_ANALYTICS_API.SUMMARY}?${cocustomer_project_ids}&${coagent_project_ids}`,
          params: {
            start_date,
            end_date,
          },
        }),
      }),
      getProjectById: build.query<Project, { id: number | string }>({
        query: ({ id }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}`,
          };
        },
        providesTags: (result, error, { id }) => [{ type: 'ProjectById', id }],
      }),
      getProjectTeamMembers: build.query<ProjectTeamMember[], { id: number | string }>({
        query: ({ id }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}`,
          };
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: teamMembers } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                draft.team_members = teamMembers;
              }),
            );
          } catch (err) {
            console.error('Error get project team members:', err);
          }
        },
        providesTags: (result, error, { id }) => [{ type: 'ProjectTeamMembers', id }],
      }),
      addProjectTeamMembers: build.mutation({
        query: ({ id, users, type }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}`,
            params: {
              ...(type !== '' && { project_type: type }),
            },
            method: 'POST',
            body: {
              users: users,
            },
          };
        },
        invalidatesTags: ['ProjectTeamMembers', 'ProjectById'],
      }),
      deleteProjectTeamMembers: build.mutation({
        query: ({ id, teamMemberId, type }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}/${teamMemberId}`,
            params: {
              ...(type !== '' && { project_type: type }),
            },
            method: 'DELETE',
          };
        },
        async onQueryStarted({ id, teamMemberId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                draft.team_members = draft.team_members.filter(
                  (member) => Number(member.id) !== Number(teamMemberId),
                );
              }),
            );
          } catch (err) {
            console.error('Error updating project team members:', err);
          }
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectTeamMembers', id }],
      }),
      newLimitProjectTeamMembers: build.mutation({
        query: ({ id, teamMemberId, limit }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}/${teamMemberId}/${PATH_PROJECTS_API.NEW_LIMIT}`,
          method: 'PUT',
          body: {
            limit,
          },
        }),
        async onQueryStarted({ id, teamMemberId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedTeamMember } = await queryFulfilled;
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                const currentMember = draft.team_members.find(
                  (member) => member.id === updatedTeamMember.id,
                );

                if (currentMember) {
                  currentMember.requests_left = updatedTeamMember.requests_left;
                }
              }),
            );
          } catch (err) {
            console.error('Error updating project team members:', err);
          }
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectTeamMembers', id }],
      }),
      updateProject: build.mutation({
        query: ({
          projectId,
          name,
          ai_reply_generation,
          ai_text_lense,
          cocbi_project_id = 0,
          use_cocbi_model = false,
          ...rest
        }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}`,
            method: version === API_VERSION.V1 ? 'PUT' : 'PATCH',
            body: {
              name,
              cocbi_project_id,
              use_cocbi_model,
              ai_reply_generation,
              ai_text_lense,
              ...rest,
            },
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                const currentProject = draft.find((project) => project.id === updatedProject.id);
                Object.assign(currentProject, updatedProject);
              }),
            );
            const { integrations, ...rest } = updatedProject;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  Object.assign(draft, rest);
                },
              ),
            );
          } catch {}
        },
        invalidatesTags: ['Projects', 'ProjectById'],
      }),
      deleteProject: build.mutation({
        query: ({ projectId, ...rest }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}`,
            method: 'DELETE',
            body: rest,
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                return draft?.filter((project) => +project.id !== +projectId);
              }),
            );
          } catch {}
        },
        invalidatesTags: ['Projects'],
      }),
      createProject: build.mutation({
        query: (body) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}`,
            method: 'POST',
            body,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            const { data: createdProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                draft?.push(createdProject);
              }),
            );
          } catch {}
        },
      }),
      activateProject: build.mutation({
        query: ({ projectId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/activate`,
            method: 'PUT',
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                const currentProject = draft.find((project) => project.id === updatedProject.id);
                currentProject.is_active = updatedProject.is_active;
              }),
            );
          } catch {}
        },
        invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectById', id: projectId }],
      }),
      deactivateProject: build.mutation({
        query: ({ projectId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/deactivate`,
            method: 'PUT',
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                const currentProject = draft.find((project) => project.id === updatedProject.id);
                currentProject.is_active = updatedProject.is_active;
              }),
            );
          } catch {}
        },
        invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectById', id: projectId }],
      }),
      projectDemoMode: build.mutation({
        query: ({ id, project_type, enable }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.DEMO_MODE}/${id}`,
          method: 'POST',
          params: {
            project_id: id,
            project_type,
            enable,
          },
        }),
        async onQueryStarted({ id, enable }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                draft.is_demo_mode = enable;
              }),
            );
          } catch (err) {
            console.error('Error add project integration:', err);
          }
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectById', id }],
      }),
    };
  },
});

export const {
  useUpdateProjectMutation,
  useGetProjectByIdQuery,
  useGetAllProjectsQuery,
  useDeleteProjectMutation,
  useCreateProjectMutation,
  useGetProjectTeamMembersQuery,
  useAddProjectTeamMembersMutation,
  useDeleteProjectTeamMembersMutation,
  useNewLimitProjectTeamMembersMutation,
  useActivateProjectMutation,
  useDeactivateProjectMutation,
  useProjectDemoModeMutation,
  useGetProjectsStatisticsSummaryQuery,
} = projectsService;
